import { CondoService } from '@api/service/condo.service';
import { Component, Inject } from '@angular/core';
import { Condo } from '@api/model/condo';
import { NZ_MODAL_DATA } from 'ng-zorro-antd/modal';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { formatCnpj, formatCpf } from '@api/util/formatters';
import { formatCep, formatPhone, isValidCNPJ, isValidCPF } from '@api/utils';
import { debounceTime, distinctUntilChanged, timeout } from 'rxjs/operators';
import { AddressService } from '../../service/address.service';
import { NzNotificationService } from 'ng-zorro-antd/notification';

@Component({
  selector: 'app-modal-create-financial-responsible',
  templateUrl: './modal-create-financial-responsible.component.html',
  styleUrls: ['./modal-create-financial-responsible.component.scss']
})
export class ModalCreateFinancialResponsible {
  condo: Condo;

  form: UntypedFormGroup;
  hasError: boolean = false;
  totalValue: string = '';
  document = { number: '', type: '' };

  constructor(
    private fb: UntypedFormBuilder,
    private addressService: AddressService,
    private condoService: CondoService,
    private notification: NzNotificationService,
    @Inject(NZ_MODAL_DATA) data: { condo: Condo }
  ) {
    this.condo = data.condo;

    let cpfCnpjValidator = (c: UntypedFormControl) => {
      if (c.value.length > 14) {
        return c.value && isValidCNPJ(c.value) ? null : { isValidCpfCnpj: { valid: false } };
      } else {
        return c.value && isValidCPF(c.value) ? null : { isValidCpfCnpj: { valid: false } };
      }
    };

    this.form = this.fb.group({
      name: new UntypedFormControl(null, [Validators.required]),
      cpfCnpj: ['', [Validators.required, cpfCnpjValidator]],
      document: [],
      phone: new UntypedFormControl(null, [Validators.required, Validators.maxLength(15), Validators.minLength(15)]),
      email: new UntypedFormControl(null, [Validators.required, Validators.email]),
      zipcode: new UntypedFormControl(null, [Validators.required, Validators.maxLength(9), Validators.minLength(9)]),
      address: new UntypedFormControl(null, [Validators.required]),
      number: new UntypedFormControl(null),
      state: new UntypedFormControl(null, [Validators.required]),
      city: new UntypedFormControl(null, [Validators.required]),
      neighborhood: new UntypedFormControl(null, [Validators.required]),
      complement: new UntypedFormControl(null),
      implantation: new UntypedFormControl(null),
      numberOfResidences: new UntypedFormControl(null),
      contractStartDate: new UntypedFormControl(null, [Validators.required]),
      unitValue: new UntypedFormControl(null),
      integrations: new UntypedFormControl(null),
      whiteLabelValue: new UntypedFormControl(null),
      whiteLabelMaintenance: new UntypedFormControl(null),
      otherValue: new UntypedFormControl(null),
      discount: new UntypedFormControl(null),
      totalValue: [],
      comments: new UntypedFormControl(null)
    });

    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.totalValueSum();
    });

    this.form
      .get('cpfCnpj')
      ?.valueChanges.pipe(takeUntilDestroyed())
      .subscribe(v => {
        v = (v || '').replace(/\D/g, '');
        if (v.length > 11) {
          v = formatCnpj(v);
        } else {
          v = formatCpf(v);
        }
        this.form.get('cpfCnpj')?.setValue(v, { emitEvent: false });
      });

    this.form
      .get('phone')
      ?.valueChanges.pipe(debounceTime(50), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe(phone => {
        if (!phone) {
          return;
        }
        const val = phone.trim();
        this.form.get('phone')?.setValue(formatPhone(phone), { emitEvent: false });
      });

    this.form
      .get('zipcode')
      ?.valueChanges.pipe(debounceTime(50), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe((zipcode: string) => {
        if (!zipcode) {
          return;
        }
        const val = zipcode.trim();
        this.searchzipcode(val);
        this.form.get('zipcode')?.setValue(formatCep(zipcode), { emitEvent: false });
      });

    this.timeout();
  }

  timeout() {
    setTimeout(() => {
      if (this.condo.sale) {
        this.editForm();
      }
    }, 0);
  }

  searchzipcode(code: string) {
    if (code.length < 9 || code.length > 9) {
      return;
    }
    this.addressService.getAddress(code.replace('-', '')).subscribe({
      next: (data: any) => {
        if (!('erro' in data)) {
          this.form.get('address')?.setValue(data.logradouro || '');
          this.form.get('city')?.setValue(data.localidade || '');
          this.form.get('state')?.setValue(data.uf || '');
          this.form.get('neighborhood')?.setValue(data.bairro || '');
        } else {
          this.form.get('address')?.setValue('');
          this.form.get('city')?.setValue('');
          this.form.get('state')?.setValue('');
          this.form.get('neighborhood')?.setValue('');
          this.hasError = true;
        }
        this.form.get('address')?.markAsTouched();
        this.form.get('city')?.markAsTouched();
        this.form.get('state')?.markAsTouched();
        this.form.get('neighborhood')?.markAsTouched();
      },
      error: err => {
        this.form.get('address')?.setValue('');
        this.form.get('city')?.setValue('');
        this.form.get('state')?.setValue('');
        this.form.get('neighborhood')?.setValue('');
        this.hasError = true;
      }
    });
  }

  submitForm(): void {
    if (this.form.valid) {
      this.totalValueSum();
      this.form.get('totalValue')?.setValue(this.totalValue);
      if (this.form.get('cpfCnpj')?.value.length === 14) {
        this.document.number = this.form.get('cpfCnpj')?.value.replace(/\D/g, '');
        this.document.type = 'CPF';
      } else {
        this.document.number = this.form.get('cpfCnpj')?.value.replace(/\D/g, '');
        this.document.type = 'CNPJ';
      }
      this.form.get('document')?.setValue(this.document);
      this.condoService.subscribeCondoWithSystemAdmin(this.condo._id, this.form.value).subscribe({
        next: res => {
          this.notification.create('success', 'Responsavel financeiro', 'Cadastrado com sucesso');
        },
        error: err => {
          this.notification.create('error', 'Responsavel financeiro', 'Não Cadastrado');
          console.log(err);
        }
      });
    } else {
      for (const key in this.form.controls) {
        this.form.controls[key].markAsDirty();
        this.form.controls[key].updateValueAndValidity();
      }
    }
  }

  totalValueSum() {
    this.totalValue = (
      Number(this.form.get('implantation')?.value) +
      Number(this.form.get('integrations')?.value) +
      Number(this.form.get('whiteLabelValue')?.value) +
      Number(this.form.get('whiteLabelMaintenance')?.value) +
      Number(this.form.get('unitValue')?.value) * Number(this.form.get('numberOfResidences')?.value) +
      Number(this.form.get('otherValue')?.value) -
      Number(this.form.get('discount')?.value)
    ).toFixed(2);
  }

  editForm() {
    this.form.get('name')?.setValue(this.condo.sale?.name);
    this.form.get('cpfCnpj')?.setValue(this.condo.sale.document?.number);
    this.form.get('phone')?.setValue(this.condo.sale?.phone);
    this.form.get('email')?.setValue(this.condo.sale?.email);
    this.form.get('zipcode')?.setValue(this.condo.sale?.zipcode);
    this.form.get('number')?.setValue(this.condo.sale?.number);
    this.form.get('complement')?.setValue(this.condo.sale?.complement);
    this.form.get('implantation')?.setValue(this.condo.sale?.implantation);
    this.form.get('numberOfResidences')?.setValue(this.condo.sale?.numberOfResidences);
    this.form.get('unitValue')?.setValue(this.condo.sale?.unitValue);
    this.form.get('integrations')?.setValue(this.condo.sale?.integrations);
    this.form.get('whiteLabelValue')?.setValue(this.condo.sale?.whiteLabelValue);
    this.form.get('whiteLabelMaintenance')?.setValue(this.condo.sale?.whiteLabelMaintenance);
    this.form.get('otherValue')?.setValue(this.condo.sale?.otherValue);
    this.form.get('discount')?.setValue(this.condo.sale?.discount);
    this.form.get('comments')?.setValue(this.condo.sale?.comments);
    this.form.get('contractStartDate')?.setValue(this.condo.sale?.contractStartDate);
  }
}
